/**
 * LA DURACIÓN DE UNA SESIÓN DE FOCO, fuera del componente.
 *
 * Esto vivía dentro de `foco.tsx` y por eso no se podía probar. No es un
 * detalle de organización: el fallo de «Otra no hacía nada» —pulsarla con 25
 * minutos puestos pedía 25 minutos, 25 sigue siendo preset, y el control no
 * aparecía nunca— se cazó a mano en el navegador, cuando una línea de test lo
 * habría cazado solo. La regla de esta casa ya estaba escrita en `domain`: lo
 * que no pinta, se prueba.
 */

/** Los tres del camino rápido: los que usa casi todo el mundo casi siempre. */
export const DURACIONES = [15, 25, 50] as const;

/** Cinco minutos abajo porque por debajo de diez la sesión no puntúa, y no
 *  tiene sentido ofrecer un temporizador que no cuenta para nada; cuatro horas
 *  arriba porque a partir de ahí no es una sesión de foco, es una jornada. */
export const MIN_MINUTOS = 5;
export const MAX_MINUTOS = 240;
export const PASO_MINUTOS = 5;

/** Lo que el segmentado manda: uno de los presets, o la casilla libre. */
export const OTRA = 'otra';

export type EstadoDuracion = {
  minutos: number;
  /** Si la casilla marcada es «Otra». Ver `eligeDuracion`. */
  libre: boolean;
};

/**
 * Deja el número dentro de los topes, redondeado.
 *
 * El `respaldo` existe por un caso real: el campo es un `<input type="number">`
 * y `Number('')` es 0, pero `Number('abc')` es **NaN**, y NaN atraviesa `max` y
 * `min` sin que ninguno lo detenga —`Math.max(5, NaN)` es NaN—. Sin esta
 * guarda, un valor no numérico se colaba hasta el estado y el reloj se quedaba
 * en «NaN:00».
 */
export function acotaMinutos(n: number, respaldo: number = MIN_MINUTOS): number {
  if (!Number.isFinite(n)) return respaldo;
  return Math.min(MAX_MINUTOS, Math.max(MIN_MINUTOS, Math.round(n)));
}

export function esPreset(minutos: number): boolean {
  return (DURACIONES as readonly number[]).includes(minutos);
}

/**
 * Qué pasa al tocar una casilla del segmentado.
 *
 * **«Otra» es estado propio y no se deduce de si los minutos son un preset.**
 * Ahí estaba el fallo: querer ajustar a mano y estar en un número redondo son
 * cosas distintas. Pulsar «Otra» con 25 puestos tiene que abrir el ajuste EN 25,
 * no no-hacer-nada porque 25 resulte ser uno de los tres.
 */
export function eligeDuracion(actual: EstadoDuracion, opcion: string): EstadoDuracion {
  if (opcion === OTRA) return { minutos: actual.minutos, libre: true };
  return { minutos: acotaMinutos(Number(opcion), actual.minutos), libre: false };
}

/** Qué casilla se ve marcada. Siempre hay una: sin esto, unos minutos que no
 *  son preset dejarían la pieza del segmentado en la primera casilla y el
 *  control diría «15» mientras el reloj dice otra cosa. */
export function casillaMarcada(estado: EstadoDuracion): string {
  return estado.libre ? OTRA : String(estado.minutos);
}

/** Lo que suma la sesión al terminarla, según el baremo del dominio: por debajo
 *  de diez minutos no cuenta, a partir de cuarenta y cinco es el doble. Se dice
 *  antes de empezar; enterarse después de haberla hecho es peor. */
export function puntosPrevistos(minutos: number): number {
  if (minutos < 10) return 0;
  return minutos >= 45 ? 30 : 15;
}
